import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, UserCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isFollowingBuddha, toggleFollowBuddha, getFollowerCount } from "@/lib/community.functions";

export function FollowBuddhaButton({ className }: { className?: string }) {
  const qc = useQueryClient();
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSignedIn(Boolean(data.session)));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSignedIn(Boolean(s)));
    return () => sub.subscription.unsubscribe();
  }, []);

  const isFollowingFn = useServerFn(isFollowingBuddha);
  const toggleFn = useServerFn(toggleFollowBuddha);
  const countFn = useServerFn(getFollowerCount);

  const following = useQuery({
    queryKey: ["follow-buddha", signedIn],
    queryFn: () => (signedIn ? isFollowingFn() : Promise.resolve(false)),
    staleTime: 30_000,
  });
  const count = useQuery({ queryKey: ["follow-buddha-count"], queryFn: () => countFn(), staleTime: 60_000 });

  const mut = useMutation({
    mutationFn: () => toggleFn(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["follow-buddha"] });
      qc.invalidateQueries({ queryKey: ["follow-buddha-count"] });
    },
  });

  const isFollowing = following.data === true;
  const followerCount = count.data?.count ?? 0;

  if (!signedIn) {
    return (
      <a
        href="/auth"
        className={`inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian shadow-gold min-h-[40px] ${className ?? ""}`}
      >
        <UserPlus size={14} /> Follow Buddha KA
      </a>
    );
  }

  return (
    <div className={`inline-flex items-center gap-3 ${className ?? ""}`}>
      <button
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors min-h-[40px] ${
          isFollowing
            ? "border border-gold/60 bg-obsidian/60 text-gold-luminous hover:bg-obsidian/80"
            : "bg-gold-gradient text-obsidian shadow-gold"
        }`}
      >
        {isFollowing ? <UserCheck size={14} /> : <UserPlus size={14} />}
        {isFollowing ? "Following" : "Follow Buddha KA"}
      </button>
      {followerCount > 0 && (
        <span className="text-xs text-muted-foreground">{followerCount.toLocaleString()} followers</span>
      )}
    </div>
  );
}
